import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Star } from 'lucide-react';
import { Piko } from '../components/Piko';
import { ClayButton } from '../components/ClayButton';
import { audio } from '../core/audio';
import { setState } from '../core/storage';
import { lang, t } from '../core/i18n';
import { getGame, gameName, proWorldsWithGames, worldGames } from '../content/games';

// Cinematic "Pengembaraan Seterusnya" after completing the free journey.
export default function NextAdventures() {
  const nav = useNavigate();
  const en = lang() === 'en';

  useEffect(() => {
    audio.init();
    audio.music('theme_calm');
    audio.playVoice('next_adventure');
    setState({ freeJourneyDone: true });
  }, []);

  const proWorlds = proWorldsWithGames();

  return (
    <div className="fixed inset-0 overflow-y-auto" style={{ background: 'linear-gradient(180deg,#1D3557 0%,#3E5C8A 40%,#FCF9F2 100%)' }} data-testid="next-adventures">
      <div className="max-w-md mx-auto px-6 py-10 flex flex-col items-center">
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
          <div className="flex gap-1 justify-center mb-4">
            {[0, 1, 2].map((i) => (
              <motion.div key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.4 + i * 0.25, type: 'spring' }}>
                <Star size={40} className="text-sunshine fill-sunshine drop-shadow-lg" />
              </motion.div>
            ))}
          </div>
          <Piko pose="celebrate" size={150} />
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          className="font-heading text-3xl font-bold text-white text-center mt-4 drop-shadow-md">
          {en ? 'Your Next Adventures' : 'Pengembaraan Seterusnya'}
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
          className="font-body text-white/85 text-center mt-2">
          {en ? 'Piko found new worlds to explore!' : 'Piko jumpa dunia baharu untuk diterokai!'}
        </motion.p>

        <div className="w-full flex flex-col gap-6 mt-8">
          {proWorlds.map((w, si) => (
            <motion.div key={w.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 + si * 0.3 }}>
              <h2 className="font-heading text-lg font-bold text-white/95 drop-shadow">{t(w.nameKey)}</h2>
              <p className="font-body text-xs text-white/75" data-testid={`adventure-objective-${w.id}`}>{w.objective[en ? 'en' : 'ms']}</p>
              {/* The concrete, testable promise — this is what a parent is
                  actually buying, not a count of games. */}
              {w.witness && (
                <p className="font-body text-xs font-semibold text-sunshine/95 mb-2 leading-snug" data-testid={`adventure-witness-${w.id}`}>
                  {w.witness[en ? 'en' : 'ms']}
                </p>
              )}
              <div className="grid grid-cols-2 gap-3">
                {worldGames(w.id).map((gid) => {
                  const g = getGame(gid);
                  return (
                    <div key={gid} className="relative overflow-hidden rounded-[1.8rem] h-28 shadow-xl" style={{ border: '5px solid rgba(255,255,255,0.9)' }} data-testid={`preview-${gid}`}>
                      <motion.div className="absolute inset-0" animate={{ scale: [1, 1.06, 1] }} transition={{ repeat: Infinity, duration: 8 }}
                        style={{ backgroundImage: `url(${w.bg})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                      <div className="absolute inset-0 bg-white/25 backdrop-blur-[1.5px]" />
                      <div className="relative h-full flex flex-col justify-between p-2.5">
                        <div className="flex justify-between">
                          <span className="bg-navy text-sunshine font-heading font-bold text-[10px] px-2 py-0.5 rounded-full">PRO</span>
                          <div className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center"><Lock size={13} className="text-navy/60" /></div>
                        </div>
                        <p className="font-heading text-sm font-bold text-navy leading-tight">{gameName(g, en)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="flex flex-col items-center gap-3 mt-9 w-full">
          <ClayButton testId="adventures-back-btn" onClick={() => { audio.stopMusic(); nav('/home'); }} className="w-full">
            {en ? 'Back to my games' : 'Kembali ke permainan saya'}
          </ClayButton>
          <button data-testid="adventures-parent-btn" onClick={() => nav('/parent-gate?next=upgrade')}
            className="font-body text-navy/60 underline min-h-[48px]">{en ? 'Parent Area' : 'Ruang Ibu Bapa'}</button>
        </motion.div>
      </div>
    </div>
  );
}
